import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Users } from "lucide-react";
import { initials } from "@/lib/format";
import { toast } from "sonner";

const FollowList = ({ mode }) => {
  // mode: 'followers' | 'following'
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(mode === "followers" ? "/me/followers" : "/me/following");
      setUsers(data.users);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [mode]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.username}/follow`);
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_following: data.following } : x));
    } catch (e) { toast.error(apiError(e)); }
  };

  const title = mode === "followers" ? "Mes abonnés" : "Mes abonnements";

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl flex items-center gap-2" data-testid="follow-list-title"><Users className="h-6 w-6 text-primary" /> {title}</h1>
      {loading ? (
        <div className="space-y-2">{[0,1,2,3].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><Users className="h-6 w-6" /></div>
          <p className="font-medium text-foreground">{mode === "followers" ? "Aucun abonné pour le moment" : "Vous ne suivez personne"}</p>
          <p className="text-sm mt-1">Explorez et abonnez-vous à des créateurs.</p>
          <Button className="mt-4 rounded-xl" onClick={() => navigate("/explorer")}>Explorer</Button>
        </div>
      ) : (
        <div className="space-y-1" data-testid="follow-list">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-3 rounded-xl p-2 hover:bg-secondary/60 transition-colors" data-testid={`follow-row-${u.username}`}>
              <button onClick={() => navigate(`/c/${u.username}`)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                <Avatar className="h-11 w-11">
                  {u.avatar && <AvatarImage src={u.avatar} alt={u.username} />}
                  <AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(u.username)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0"><p className="text-sm font-medium truncate">{u.username}</p><p className="text-xs text-muted-foreground truncate">@{u.username}</p></div>
              </button>
              {!u.is_me && (
                <Button size="sm" variant={u.is_following ? "outline" : "default"} className="rounded-xl shrink-0" onClick={() => toggle(u)} data-testid={`follow-toggle-${u.username}`}>
                  {u.is_following ? "Abonné" : "S'abonner"}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FollowList;
